import { z } from "zod";

import { INVESTMENT_OBJECTIVE_VALUES } from "~/v2/api/rest/account";
import {
  BUSINESS_TYPES,
  ENTITY_TYPES,
  FUNDING_SOURCES,
  INTENDED_ACCOUNT_USES,
  REQUIRED_ERR_MSG,
  USA_STATES_MAP,
} from "../../constants";
import { zIsoDate, zNonEmptyString, zPhoneNumber } from "../../util";

export const zBusinessInfo = z.object({
  is_legal_entity: z.literal(true),
  legal_name: zNonEmptyString,
  country_of_incorporation: zNonEmptyString,
  state_of_incorporation: z
    .enum(["", ...Object.keys(USA_STATES_MAP)], {
      error: "State is required for USA",
    })
    .optional(),
  date_of_incorporation: zIsoDate.refine(
    (d) => d <= new Date().toISOString().substring(0, 10),
    "Date cannot be in the future",
  ),
  // labelled as "Business Type"
  entity_type: z.enum(ENTITY_TYPES, {
    error: REQUIRED_ERR_MSG,
  }),
  entity_type_other_description: z.string().optional(),
  is_exempt_from_tax_under_501a: z.boolean(),
  is_participating_fatca: z.boolean(),
  email: z.string().email(),
  phone_number: zPhoneNumber,
  country: zNonEmptyString,
  annual_income_min: z.number(),
  annual_income_max: z.number(),
  total_net_worth_min: z.number(),
  total_net_worth_max: z.number(),
  liquid_net_worth_min: z.number(),
  liquid_net_worth_max: z.number(),
  intended_use_of_account: z.enum(INTENDED_ACCOUNT_USES, {
    error: REQUIRED_ERR_MSG,
  }),
  funding_source: z.enum(FUNDING_SOURCES, {
    error: REQUIRED_ERR_MSG,
  }),
  investment_objective: z.enum(INVESTMENT_OBJECTIVE_VALUES, {
    error: REQUIRED_ERR_MSG,
  }),
  type_of_business: z.enum(BUSINESS_TYPES, {
    error: REQUIRED_ERR_MSG,
  }),
  type_of_business_other_free_text: z.string().optional(),
  tax_id_type: zNonEmptyString,
  tax_id: z.string().optional(),
  country_of_tax_residence: zNonEmptyString,
  street_address: zNonEmptyString,
  city: zNonEmptyString,
  state: zNonEmptyString,
  postal_code: zNonEmptyString,
  unit: z.string().optional(),
  business_registration_number: zNonEmptyString,
});

export type BusinessInfo = z.infer<typeof zBusinessInfo>;
